'use client';
import React, { useState, useEffect, useMemo } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { Gift, Copy, Share2, Wallet, TrendingUp, Users, CheckCircle, Clock, AlertCircle, ArrowDownLeft, ArrowUpRight, Loader2, IndianRupee, MessageCircle, Mail, Link2, Smartphone, ChevronRight, BarChart3, Award, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';


interface WalletData {
  balance: number;
  lifetime_earned: number;
  pending_cashback: number;
  total_withdrawn: number;
}

interface Referral {
  id: string;
  referred_email: string;
  status: string;
  plan_type: string | null;
  cashback_amount: number;
  created_at: string;
  completed_at: string | null;
  referred: { full_name: string; email: string } | null;
}

interface Transaction {
  id: string;
  amount: number;
  transaction_type: string;
  description: string;
  status: string;
  created_at: string;
}

interface WithdrawalRequest {
  id: string;
  amount: number;
  upi_id: string;
  status: string;
  created_at: string;
}

interface CashbackConfig {
  plan_type: string;
  cashback_amount: number;
}

type ReferEarnTab = 'overview' | 'referrals' | 'wallet' | 'withdraw';

export default function ReferEarnDashboard() {
  const { user, profile } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<ReferEarnTab>('overview');
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [cashbackConfig, setCashbackConfig] = useState<CashbackConfig[]>([]);

  // Setup modal
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [upiId, setUpiId] = useState('');
  const [upiHolderName, setUpiHolderName] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  // Withdraw modal
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const profileType = profile?.profile_type;
  const isEligible = profileType === 'end_user' || profileType === 'channel_partner';

  useEffect(() => {
    if (user && isEligible) loadData();
    else setLoading(false);
  }, [user, isEligible]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [profileRes, walletRes, referralsRes, txRes, withdrawRes, configRes] = await Promise.all([
        supabase.from('user_profiles').select('referral_code, refer_earn_enabled, upi_id, upi_holder_name').eq('id', user.id).maybeSingle(),
        supabase.from('cashback_wallet').select('*').eq('user_id', user.id).maybeSingle(),
        supabase.from('referrals').select('*, referred:user_profiles!referrals_referred_user_id_fkey(full_name, email)').eq('referrer_id', user.id).order('created_at', { ascending: false }),
        supabase.from('cashback_transactions').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(20),
        supabase.from('withdrawal_requests').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('cashback_config').select('*'),
      ]);

      if (profileRes.data) {
        setReferralCode(profileRes.data.referral_code || '');
        setIsEnabled(profileRes.data.refer_earn_enabled || false);
        setUpiId(profileRes.data.upi_id || '');
        setUpiHolderName(profileRes.data.upi_holder_name || '');
      }
      if (walletRes.data) setWallet(walletRes.data);
      if (referralsRes.data) setReferrals(referralsRes.data as Referral[]);
      if (txRes.data) setTransactions(txRes.data as Transaction[]);
      if (withdrawRes.data) setWithdrawals(withdrawRes.data as WithdrawalRequest[]);
      if (configRes.data) setCashbackConfig(configRes.data);
    } catch (err) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    if (!upiId.trim() || !upiHolderName.trim()) {
      toast.error('Please fill in all UPI details');
      return;
    }
    setSetupLoading(true);
    try {
      const res = await fetch('/api/referral/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upiId: upiId.trim(), upiHolderName: upiHolderName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setReferralCode(data.referralCode);
        setIsEnabled(true);
        setShowSetupModal(false);
        toast.success('Refer & Earn activated! Your referral code is ready.');
        await loadData();
      } else {
        toast.error(data.error || 'Setup failed');
      }
    } catch {
      toast.error('Setup failed');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 500) { toast.error('Minimum withdrawal is ₹500'); return; }
    if (!upiId) { toast.error('Please add UPI details first'); return; }
    setWithdrawLoading(true);
    try {
      const res = await fetch('/api/referral/withdraw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, upiId, upiHolderName }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        await loadData();
      } else {
        toast.error(data.error || 'Withdrawal failed');
      }
    } catch {
      toast.error('Withdrawal failed');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/sign-up-login-screen?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(`Join PropdataX with my referral code ${referralCode} and get access to Pune's best real estate data platform! ${window.location.origin}/sign-up-login-screen?ref=${referralCode}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent('Join PropdataX - Pune Real Estate Intelligence');
    const body = encodeURIComponent(`Hi,\n\nI'd like to invite you to PropdataX - Pune's most data-dense real estate platform.\n\nUse my referral code: ${referralCode}\n\nSign up here: ${window.location.origin}/sign-up-login-screen?ref=${referralCode}\n\nBest regards`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  // Monthly earnings chart data
  const monthlyData = React.useMemo(() => {
    const months: Record<string, number> = {};
    referrals.filter(r => r.status === 'completed').forEach(r => {
      const month = new Date(r.completed_at || r.created_at).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
      months[month] = (months[month] || 0) + (r.cashback_amount || 0);
    });
    return Object.entries(months).slice(-6).map(([month, amount]) => ({ month, amount }));
  }, [referrals]);

  const totalReferrals = referrals.length;
  const successfulReferrals = referrals.filter(r => r.status === 'completed').length;
  const pendingReferrals = referrals.filter(r => r.status === 'pending').length;
  const conversionRate = totalReferrals > 0 ? Math.round((successfulReferrals / totalReferrals) * 100) : 0;

  const monthlyCashback = cashbackConfig.find(c => c.plan_type === 'monthly')?.cashback_amount || 500;
  const yearlyCashback = cashbackConfig.find(c => c.plan_type === 'yearly')?.cashback_amount || 2000;

  const tabs: { key: ReferEarnTab; label: string; icon: React.ElementType }[] = [
    { key: 'overview', label: 'Overview', icon: BarChart3 },
    { key: 'referrals', label: 'Referrals', icon: Users },
    { key: 'wallet', label: 'Wallet', icon: Wallet },
    { key: 'withdraw', label: 'Withdraw', icon: IndianRupee },
  ];

  if (!isEligible) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Gift size={28} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Refer & Earn Not Available</h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          The Refer & Earn program is available for End Users and Channel Partners only.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-amber-400" />
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className="space-y-6">
        {/* Activation banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-amber-600/20 border border-amber-500/30 p-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                <Gift size={24} className="text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">Activate Refer & Earn</h2>
                <p className="text-sm text-muted-foreground">Start earning cashback for every successful referral</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-card/60 border border-border rounded-xl p-4">
                <p className="text-2xl font-bold text-amber-400">₹{monthlyCashback}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Per monthly plan referral</p>
              </div>
              <div className="bg-card/60 border border-border rounded-xl p-4">
                <p className="text-2xl font-bold text-emerald-400">₹{yearlyCashback}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Per yearly plan referral</p>
              </div>
            </div>

            <button
              onClick={() => setShowSetupModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-all duration-200"
            >
              <Gift size={18} />
              Activate Refer & Earn
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-base font-semibold text-foreground mb-4">How It Works</h3>
          <div className="space-y-4">
            {[
              { step: '1', title: 'Activate & Get Your Code', desc: 'Add your UPI details and get a unique referral code', icon: Gift },
              { step: '2', title: 'Share With Your Network', desc: 'Share via WhatsApp, email, or copy your referral link', icon: Share2 },
              { step: '3', title: 'They Subscribe', desc: 'When they sign up and purchase a plan using your code', icon: Users },
              { step: '4', title: 'You Earn Cashback', desc: `₹${monthlyCashback} for monthly, ₹${yearlyCashback} for yearly plans`, icon: IndianRupee },
            ].map(({ step, title, desc, icon: Icon }) => (
              <div key={step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-amber-400">{step}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Setup Modal */}
        {showSetupModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-foreground">Setup UPI Details</h3>
                <button onClick={() => setShowSetupModal(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all"><X size={18} /></button>
              </div>
              <p className="text-sm text-muted-foreground mb-5">Your UPI details are required to receive cashback withdrawals.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">UPI ID *</label>
                  <input type="text" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="e.g. amrut@oksbi or name@paytm"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Account Holder Name *</label>
                  <input type="text" value={upiHolderName} onChange={(e) => setUpiHolderName(e.target.value)} placeholder="Full name as per bank account"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all" />
                </div>
                <div className="flex items-start gap-2 p-3 bg-amber-500/5 border border-amber-500/15 rounded-lg">
                  <AlertCircle size={13} className="text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-muted-foreground">Your UPI details are stored securely and used only for cashback payouts.</p>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowSetupModal(false)} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all">Cancel</button>
                <button onClick={handleSetup} disabled={setupLoading || !upiId.trim() || !upiHolderName.trim()}
                  className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all flex items-center justify-center gap-2">
                  {setupLoading ? <><Loader2 size={14} className="animate-spin" />Activating…</> : <><Gift size={14} />Activate</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Referral Code Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/15 via-orange-500/8 to-amber-600/15 border border-amber-500/25 p-6">
        <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-xs text-amber-400 font-semibold uppercase tracking-widest mb-1">Your Referral Code</p>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-black text-foreground tracking-widest font-mono">{referralCode}</span>
              <button onClick={copyReferralCode} className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 hover:bg-amber-500/25 transition-all" title="Copy code">
                <Copy size={16} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Share this code to earn cashback on every successful referral</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={shareWhatsApp} className="flex items-center gap-1.5 px-3 py-2 bg-green-600/20 border border-green-600/30 text-green-400 text-xs font-semibold rounded-lg hover:bg-green-600/30 transition-all">
              <MessageCircle size={13} />WhatsApp
            </button>
            <button onClick={shareEmail} className="flex items-center gap-1.5 px-3 py-2 bg-blue-600/20 border border-blue-600/30 text-blue-400 text-xs font-semibold rounded-lg hover:bg-blue-600/30 transition-all">
              <Mail size={13} />Email
            </button>
            <button onClick={copyReferralLink} className="flex items-center gap-1.5 px-3 py-2 bg-muted border border-border text-muted-foreground text-xs font-semibold rounded-lg hover:text-foreground hover:bg-muted/80 transition-all">
              <Link2 size={13} />Copy Link
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-muted/40 rounded-xl p-1 gap-1">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${activeTab === key ? 'bg-card text-amber-400 shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}>
            <Icon size={13} />{label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Stats grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Referrals', value: totalReferrals, icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
              { label: 'Successful', value: successfulReferrals, icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
              { label: 'Pending', value: pendingReferrals, icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
              { label: 'Conversion Rate', value: `${conversionRate}%`, icon: TrendingUp, color: 'text-violet-400', bg: 'bg-violet-500/10' },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="bg-card border border-border rounded-xl p-4">
                <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                  <Icon size={16} className={color} />
                </div>
                <p className="text-2xl font-bold text-foreground">{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Wallet summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Wallet Balance', value: `₹${(wallet?.balance || 0).toLocaleString('en-IN')}`, color: 'text-emerald-400' },
              { label: 'Lifetime Earned', value: `₹${(wallet?.lifetime_earned || 0).toLocaleString('en-IN')}`, color: 'text-blue-400' },
              { label: 'Pending Cashback', value: `₹${(wallet?.pending_cashback || 0).toLocaleString('en-IN')}`, color: 'text-amber-400' },
              { label: 'Total Withdrawn', value: `₹${(wallet?.total_withdrawn || 0).toLocaleString('en-IN')}`, color: 'text-muted-foreground' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-card border border-border rounded-xl p-4">
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Monthly earnings chart */}
          {monthlyData.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4">Monthly Earnings</h3>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="earningsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(v) => `₹${v}`} />
                  <Tooltip formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Earnings']} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }} />
                  <Area type="monotone" dataKey="amount" stroke="#f59e0b" strokeWidth={2} fill="url(#earningsGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Cashback rates */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2"><Award size={15} className="text-amber-400" />Cashback Rates</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-amber-500/8 border border-amber-500/20 rounded-xl">
                <p className="text-xs text-muted-foreground mb-1">Monthly Plan Referral</p>
                <p className="text-2xl font-bold text-amber-400">₹{monthlyCashback}</p>
                <p className="text-[11px] text-muted-foreground mt-1">Per successful referral</p>
              </div>
              <div className="p-4 bg-emerald-500/8 border border-emerald-500/20 rounded-xl">
                <p className="text-xs text-muted-foreground mb-1">Yearly Plan Referral</p>
                <p className="text-2xl font-bold text-emerald-400">₹{yearlyCashback}</p>
                <p className="text-[11px] text-muted-foreground mt-1">Per successful referral</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referrals Tab */}
      {activeTab === 'referrals' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Referral History</h3>
            <button onClick={loadData} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <RefreshCw size={12} />Refresh
            </button>
          </div>
          {referrals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center border border-dashed border-border rounded-xl">
              <Users size={28} className="text-muted-foreground/40 mb-3" />
              <p className="text-sm font-medium text-foreground mb-1">No referrals yet</p>
              <p className="text-xs text-muted-foreground">Share your referral code to start earning</p>
            </div>
          ) : (
            <div className="space-y-2">
              {referrals.map((ref) => (
                <div key={ref.id} className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl hover:border-amber-500/20 transition-all">
                  <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Users size={14} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{ref.referred?.full_name || ref.referred_email || 'Unknown User'}</p>
                    <p className="text-[11px] text-muted-foreground">{new Date(ref.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {ref.status === 'completed' && <p className="text-sm font-bold text-emerald-400">+₹{ref.cashback_amount}</p>}
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ref.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' : ref.status === 'fraud' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      {ref.status === 'completed' ? 'Paid' : ref.status === 'fraud' ? 'Suspended' : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Wallet Tab */}
      {activeTab === 'wallet' && (
        <div className="space-y-4">
          {/* Wallet card */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600/20 to-teal-600/20 border border-emerald-500/25 p-6">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />
            <div className="relative z-10">
              <p className="text-xs text-emerald-400 font-semibold uppercase tracking-widest mb-1">Available Balance</p>
              <p className="text-4xl font-black text-foreground">₹{(wallet?.balance || 0).toLocaleString('en-IN')}</p>
              <div className="flex items-center gap-4 mt-3">
                <div>
                  <p className="text-[11px] text-muted-foreground">Lifetime Earned</p>
                  <p className="text-sm font-bold text-blue-400">₹{(wallet?.lifetime_earned || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Pending</p>
                  <p className="text-sm font-bold text-amber-400">₹{(wallet?.pending_cashback || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Withdrawn</p>
                  <p className="text-sm font-bold text-muted-foreground">₹{(wallet?.total_withdrawn || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* UPI status */}
          <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${upiId ? 'bg-emerald-500/15' : 'bg-muted'}`}>
                <Smartphone size={15} className={upiId ? 'text-emerald-400' : 'text-muted-foreground'} />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">UPI Details</p>
                <p className="text-xs text-muted-foreground">{upiId ? upiId : 'Not configured'}</p>
              </div>
            </div>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${upiId ? 'bg-emerald-500/15 text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
              {upiId ? 'Linked' : 'Not Linked'}
            </span>
          </div>

          {/* Transaction history */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Transaction History</h3>
            {transactions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-border rounded-xl">
                <Wallet size={24} className="text-muted-foreground/40 mb-2" />
                <p className="text-xs text-muted-foreground">No transactions yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${tx.transaction_type === 'credit' ? 'bg-emerald-500/15' : 'bg-red-500/15'}`}>
                      {tx.transaction_type === 'credit' ? <ArrowDownLeft size={14} className="text-emerald-400" /> : <ArrowUpRight size={14} className="text-red-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{tx.description}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(tx.created_at).toLocaleDateString('en-IN')}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold ${tx.transaction_type === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {tx.transaction_type === 'credit' ? '+' : '-'}₹{tx.amount.toLocaleString('en-IN')}
                      </p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tx.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : tx.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                        {tx.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Withdraw Tab */}
      {activeTab === 'withdraw' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-1">Withdraw Cashback</h3>
            <p className="text-xs text-muted-foreground mb-4">Minimum withdrawal: ₹500 · Admin approval required · 3-5 business days</p>

            <div className="flex items-center justify-between p-4 bg-emerald-500/8 border border-emerald-500/20 rounded-xl mb-4">
              <div>
                <p className="text-xs text-muted-foreground">Available Balance</p>
                <p className="text-2xl font-bold text-emerald-400">₹{(wallet?.balance || 0).toLocaleString('en-IN')}</p>
              </div>
              <button
                onClick={() => setShowWithdrawModal(true)}
                disabled={!wallet || wallet.balance < 500}
                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all"
              >
                <ArrowUpRight size={15} />Withdraw
              </button>
            </div>

            {!upiId && (
              <div className="flex items-start gap-2 p-3 bg-amber-500/8 border border-amber-500/20 rounded-lg">
                <AlertCircle size={13} className="text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">Please add your UPI details to withdraw cashback.</p>
              </div>
            )}
          </div>

          {/* Withdrawal history */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Withdrawal History</h3>
            {withdrawals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-border rounded-xl">
                <IndianRupee size={24} className="text-muted-foreground/40 mb-2" />
                <p className="text-xs text-muted-foreground">No withdrawal requests yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {withdrawals.map((wr) => (
                  <div key={wr.id} className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center shrink-0">
                      <ArrowUpRight size={14} className="text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground">{wr.upi_id}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(wr.created_at).toLocaleDateString('en-IN')}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-foreground">₹{wr.amount.toLocaleString('en-IN')}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${wr.status === 'approved' || wr.status === 'paid' ? 'bg-emerald-500/15 text-emerald-400' : wr.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                        {wr.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground">Withdraw Cashback</h3>
              <button onClick={() => setShowWithdrawModal(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Amount (₹) *</label>
                <input type="number" min="500" max={wallet?.balance || 0} value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="Minimum ₹500"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all" />
                <p className="text-[11px] text-muted-foreground mt-1">Available: ₹{(wallet?.balance || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="p-3 bg-muted/40 border border-border rounded-lg">
                <p className="text-xs font-medium text-foreground mb-1">UPI Details</p>
                <p className="text-xs text-muted-foreground">{upiId} · {upiHolderName}</p>
              </div>
              <div className="flex items-start gap-2 p-3 bg-amber-500/5 border border-amber-500/15 rounded-lg">
                <AlertCircle size={13} className="text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">Withdrawal requires admin approval and will be processed within 3-5 business days.</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowWithdrawModal(false)} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all">Cancel</button>
              <button onClick={handleWithdraw} disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) < 500}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all flex items-center justify-center gap-2">
                {withdrawLoading ? <><Loader2 size={14} className="animate-spin" />Processing…</> : <><ArrowUpRight size={14} />Submit Request</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
